/**
 * Maps Firebase authentication error codes to user-friendly messages
 * This prevents exposing technical Firebase error details to users
 */
export const getAuthErrorMessage = (error) => {
  // If no error or error code, return generic message
  if (!error || !error.code) {
    return 'An unexpected error occurred. Please try again.';
  }

  const errorCode = error.code;

  // Map Firebase error codes to user-friendly messages
  const errorMessages = {
    // Authentication errors
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/user-disabled': 'This account has been disabled. Please contact support.',
    'auth/user-not-found': 'No account found with this email address.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/invalid-credential': 'Invalid email or password. Please check your credentials and try again.',
    'auth/too-many-requests': 'Too many failed attempts. Please wait a moment before trying again.',

    // Sign up errors
    'auth/email-already-in-use': 'An account with this email already exists. Please sign in instead.',
    'auth/operation-not-allowed': 'Email/password accounts are not enabled. Please contact support.',
    'auth/weak-password': 'Password is too weak. Please choose a stronger password.',

    // Google sign-in errors
    'auth/popup-closed-by-user': 'Sign-in was cancelled. Please try again.',
    'auth/popup-blocked': 'Pop-up was blocked by your browser. Please allow pop-ups and try again.',
    'auth/cancelled-popup-request': 'Sign-in was cancelled. Please try again.',

    // Network errors
    'auth/network-request-failed': 'Network error. Please check your connection and try again.',
    'auth/timeout': 'Request timed out. Please try again.',

    // General errors
    'auth/internal-error': 'An internal error occurred. Please try again later.',
    'auth/invalid-api-key': 'Configuration error. Please contact support.',
    'auth/app-deleted': 'Service unavailable. Please contact support.',
    'auth/app-not-authorized': 'Service not authorized. Please contact support.',

    // Custom validation errors (for client-side validation)
    'validation/passwords-dont-match': 'Passwords do not match.',
    'validation/password-too-short': 'Password must be at least 6 characters long.',
    'validation/invalid-display-name': 'Please enter a valid display name.'
  };

  // Return mapped message or a generic fallback
  return errorMessages[errorCode] || 'Something went wrong. Please try again.';
};

/**
 * Helper function to create a validation error object
 * for client-side validation errors
 */
export const createValidationError = (code, message) => {
  return {
    code: `validation/${code}`,
    message: message || getAuthErrorMessage({ code: `validation/${code}` })
  };
};
